/* eslint-disable no-restricted-syntax */
/* eslint-disable no-plusplus */
/* eslint-disable prefer-destructuring */

/**
 * 중복 파일 및 폴더 처리를 위한 유틸리티
 */
import { Modal } from 'antd';
import { getBaseName, getFileExtension } from 'utils/upload';

/**
 * 파일명이 현재 디렉토리 파일 목록에 존재하는지 확인
 * @param {string} fileName - 확인할 파일명
 * @param {Array} directoryFiles - 디렉토리 파일 목록
 * @param {string} filePath - 파일 경로 (폴더 업로드 경우)
 * @param {boolean} isFolder - 폴더 업로드 여부
 * @returns {boolean} 중복 여부
 */
export const isFileNameDuplicate = (fileName, directoryFiles, filePath, isFolder) => {
  if (!directoryFiles || !Array.isArray(directoryFiles)) return false;

  // 폴더 업로드일 경우 경로까지 고려해야 함..
  if (isFolder && filePath && filePath.includes('/')) {
    // 파일이 폴더 내부에 있는 경우, 중복 체크 제외
    return false;
  }
  return directoryFiles.some((file) => file.name === fileName);
};

/**
 * 중복된 파일명에 대해 고유한 새 파일명 생성 (파일명(숫자).확장자 형식)
 * @param {string} fileName - 원본 파일명
 * @param {Array} directoryFiles - 디렉토리 파일 목록
 * @returns {string} 고유한 파일명
 */
export const createUniqueFileName = (fileName, directoryFiles) => {
  const extension = getFileExtension(fileName);
  const baseName = getBaseName(fileName);

  // "(숫자)" 패턴이 있는지 확인 (예: "file(1).txt")
  const pattern = /^(.*)\((\d+)\)$/;
  const match = baseName.match(pattern);

  let nameWithoutNumber = baseName;
  let counter = 1;

  // 이미 "(숫자)" 패턴이 있는 경우, 해당 숫자 추출하여 증가
  if (match && match.length === 3) {
    nameWithoutNumber = match[1];
    counter = parseInt(match[2], 10) + 1;
  }
  const extensionWithDot = extension ? `.${extension}` : '';

  // 중복되지 않는 이름 찾기
  let newFileName = `${nameWithoutNumber}(${counter})${extensionWithDot}`;
  while (isFileNameDuplicate(newFileName, directoryFiles)) {
    counter++;
    newFileName = `${nameWithoutNumber}(${counter})${extensionWithDot}`;
  }

  return newFileName;
};

/**
 * 중복 파일에 대한 확인 모달 표시
 * @param {Object} localeResource - 로케일 리소스
 * @returns {boolean} 사용자 확인 결과 (true: 사본으로 저장, false: 취소)
 */
export const showDuplicateConfirmModal = (localeResource) => {
  return new Promise((resolve) => {
    Modal.confirm({
      title: localeResource['txt.file-transfer.confirm-duplicate-title'],
      content: localeResource['file-transfer.validation.upload.duplicate-upload'],
      okText: localeResource['txt.file-transfer.upload'],
      cancelText: localeResource['txt.cancel'],
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
};

/**
 * 중복된 파일들의 이름 변경 처리
 * @param {Array} duplicateItems - 중복된 파일 항목 배열
 * @param {Array} directoryFiles - 디렉토리 파일 목록
 * @returns {Object} 업데이트된 파일 목록과 디렉토리 파일 목록
 */
export const renameDuplicateFiles = (duplicateItems, directoryFiles) => {
  const updatedFiles = [...duplicateItems];
  const updatedDirectoryFiles = [...directoryFiles];

  for (const item of updatedFiles) {
    const newFileName = createUniqueFileName(item.fileName, updatedDirectoryFiles);
    item.fileName = newFileName;

    // 경로에 파일명이 포함된 경우 경로도 업데이트
    if (item.filePath && item.filePath.includes('/')) {
      const lastSlashIndex = item.filePath.lastIndexOf('/');
      item.filePath = item.filePath.substring(0, lastSlashIndex + 1) + newFileName;
    } else {
      item.filePath = newFileName;
    }

    // 가상 파일을 현재 디렉토리 목록에 추가 (후속 중복 검사에 활용)
    updatedDirectoryFiles.push({ name: newFileName, type: 'file' });
  }

  return {
    updatedFiles,
    updatedDirectoryFiles,
  };
};

/**
 * 폴더 업로드 시 중복된 폴더명 처리
 * @param {Array} filesToValidate - 검증할 파일 목록
 * @param {Array} currentDirectoryFiles - 현재 디렉토리 파일 목록
 * @param {boolean} isFolder - 폴더 업로드 여부
 * @returns {Array} 업데이트된 파일 목록
 */
export const handleDuplicateFolderName = (filesToValidate, currentDirectoryFiles, isFolder) => {
  // 폴더 업로드가 아니면 처리하지 않음
  if (!isFolder || !filesToValidate || filesToValidate.length === 0) {
    return filesToValidate;
  }

  // 현재 경로에 있는 폴더 목록 필터링
  const existingFolders = currentDirectoryFiles
    .filter((item) => item.type === 'dir')
    .map((item) => item.name);

  // 업로드할 파일 경로에서 루트 폴더명 추출
  const folderPaths = filesToValidate.map((file) => file.filePath);
  const rootFolderNames = new Set();

  folderPaths.forEach((path) => {
    if (path && path.includes('/')) {
      // 예: "folder/file.txt"
      const folderName = path.split('/')[0];
      rootFolderNames.add(folderName);
    }
  });

  // 중복된 폴더 확인
  const duplicateFolders = [...rootFolderNames].filter((name) => existingFolders.includes(name));

  // 중복된 폴더가 없으면 원래 파일 목록 반환
  if (duplicateFolders.length === 0) {
    return filesToValidate;
  }

  // 중복된 폴더명에 대한 새 이름 생성
  const folderNameMap = {};
  duplicateFolders.forEach((folderName) => {
    let counter = 1;
    let newFolderName = `${folderName}(${counter})`;

    // 중복되지 않는 이름 찾기
    while (existingFolders.includes(newFolderName)) {
      counter++;
      newFolderName = `${folderName}(${counter})`;
    }

    folderNameMap[folderName] = newFolderName;
  });

  // 파일 경로 업데이트
  return filesToValidate.map((file) => {
    const updatedFile = { ...file };

    // 파일 경로에서 루트 폴더 추출
    if (file.filePath && file.filePath.includes('/')) {
      const parts = file.filePath.split('/');
      const rootFolder = parts[0];

      // 루트 폴더명이 변경되어야 하는 경우
      if (folderNameMap[rootFolder]) {
        parts[0] = folderNameMap[rootFolder];
        updatedFile.filePath = parts.join('/');

        // 파일명이 루트 폴더명을 포함하는 경우 파일명도 업데이트
        if (file.fileName.startsWith(rootFolder)) {
          updatedFile.fileName = file.fileName.replace(rootFolder, folderNameMap[rootFolder]);
        }
      }
    }

    return updatedFile;
  });
};

/**
 * 백엔드 API 응답이 중복 에러인지 확인
 * @param {Object} response - API 응답 객체
 * @returns {boolean} 중복 에러 여부
 */
export const isDuplicateError = (response) => {
  if (response.data.success !== false || response.data.errorType !== 'validation') {
    return false;
  }

  // 벨리데이션 오류 항목 확인
  const validationData = response.data.data;
  if (!Array.isArray(validationData)) return false;

  // name 필드에 'duplicate' 값이 있는지 확인
  return validationData.some((item) => Array.isArray(item.name) && item.name.includes('duplicate'));
};

/**
 * 폴더 업로드 시 중복된 폴더 확인
 * @param {Array} filesToValidate - 검증할 파일 목록
 * @param {Array} currentDirectoryFiles - 현재 디렉토리 파일 목록
 * @param {boolean} isFolder - 폴더 업로드 여부
 * @returns {Object} 중복 폴더 정보 { hasDuplicates, duplicateFolders }
 */
export const checkDuplicateFolders = (filesToValidate, currentDirectoryFiles, isFolder) => {
  // 폴더 업로드가 아니면 중복 없음으로 처리
  if (!isFolder || !filesToValidate || filesToValidate.length === 0) {
    return { hasDuplicates: false, duplicateFolders: [] };
  }

  // 현재 경로에 있는 폴더 목록 필터링
  const existingFolders = currentDirectoryFiles
    .filter((item) => item.type === 'dir')
    .map((item) => item.name);

  // 업로드할 파일 경로에서 루트 폴더명 추출
  const rootFolderNames = new Set();
  filesToValidate.forEach((file) => {
    if (file.filePath.includes('/')) {
      const folderName = file.filePath.split('/')[0];
      rootFolderNames.add(folderName);
    }
  });

  // 중복된 폴더 확인
  const duplicateFolders = [...rootFolderNames].filter((name) => existingFolders.includes(name));

  return {
    hasDuplicates: duplicateFolders.length > 0,
    duplicateFolders,
  };
};
